"use client"

import { useState, useId } from "react"
import { m } from "framer-motion"
import { track } from "@/lib/analytics"
import { useLocalePrefix } from "@/lib/use-localized-navigation"
import { ChevronDownIcon } from "@/components/ui/icons"
import { sanitizeHtml } from "@/lib/sanitize"

export interface FaqAccordionItem {
  question: string
  answer: string
  slug?: string
  updatedAt?: string
  /**
   * CMS `pages` tag list. Read by FaqSection (and `lib/faq-canonical-page.ts`)
   * to decide which surface emits this FAQ's FAQPage JSON-LD — the accordion
   * UI ignores it. Optional so static fallback FAQ_ITEMS arrays don't have to
   * declare it; without it, the canonical schema page falls back to
   * `faq/<slug>`.
   */
  pages?: string[]
}

interface FaqAccordionProps {
  items: FaqAccordionItem[]
  variant?: "light" | "dark"
  allowMultiple?: boolean
}

export function FaqAccordion({
  items,
  variant = "light",
  allowMultiple = false,
}: FaqAccordionProps) {
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set())
  const baseId = useId()
  const localePrefix = useLocalePrefix()

  function toggle(index: number) {
    const wasOpen = openIndices.has(index)
    if (!wasOpen) {
      track.faqExpand(items[index].question, "faq")
    }
    setOpenIndices((prev) => {
      const next = new Set(allowMultiple ? prev : [])
      if (prev.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
    // No auto-scroll on open. The clicked trigger is always in the viewport
    // by definition (user just clicked it), and once the panel expands it can
    // push the item's bottom past the viewport, which previously made the
    // page jump downward unexpectedly on biomarker detail pages where the
    // FAQ sits near the bottom. AHD-5678.
  }

  const isDark = variant === "dark"

  return (
    <div
      className={
        isDark ? "divide-y divide-white/10" : "divide-y divide-neutral-200"
      }
    >
      {items.map((item, index) => {
        const isOpen = openIndices.has(index)
        const panelId = `${baseId}-panel-${index}`
        return (
          <div key={index}>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between py-4 text-left"
              onClick={() => toggle(index)}
              aria-expanded={isOpen}
              aria-controls={panelId}
            >
              <span
                className={
                  isDark
                    ? "type-title-md pr-4 text-white"
                    : "type-title-md pr-4 text-neutral-900"
                }
              >
                {item.question}
              </span>
              <ChevronDownIcon
                className={`ml-4 flex-shrink-0 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                } ${isDark ? "text-white/60" : "text-neutral-400"}`}
              />
            </button>
            {/*
              Always render the answer in the DOM (even when collapsed) so
              Googlebot, screen readers, and Cmd-F can find the text. The
              previous AnimatePresence + conditional-mount pattern only
              rendered the answer on click, leaving FAQ answers invisible to
              search engines on every landing page that uses this component.
              `height: 0 + overflow-hidden` collapses it visually; framer
              animates between the two states without mounting/unmounting.
            */}
            <m.div
              id={panelId}
              role="region"
              aria-hidden={!isOpen}
              inert={!isOpen}
              initial={false}
              animate={
                isOpen
                  ? { height: "auto", opacity: 1 }
                  : { height: 0, opacity: 0 }
              }
              transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
              className="overflow-hidden"
              // SSR-time inline style: every accordion starts closed
              // (`openIndices` is empty on mount), so collapse here so the
              // pre-hydration HTML doesn't flash the answer at full height
              // before framer takes over the animated properties.
              style={{ height: 0, opacity: 0 }}
            >
              <div
                className={`type-body-lg pb-4 [&_a]:underline [&_a]:underline-offset-2 [&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:font-semibold [&_li]:mb-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 ${
                  isDark
                    ? "text-white/60"
                    : "[&_a]:text-aubergine-700 text-neutral-600"
                }`}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(item.answer, localePrefix),
                }}
              />
            </m.div>
          </div>
        )
      })}
    </div>
  )
}
